import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getPackageReportsDetails } from '../../../../redux/slices/packageSlice';
import { Container, Card, Form, Row, Col, Button, Modal } from 'react-bootstrap';
import moment from 'moment';
import DataTable from "react-data-table-component";
import { arrowRight } from "../../../../assets/svg/Svg";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { 
  FilePdf, 
  FileXls, 
  MagnifyingGlass, 
  Calendar, 
  Package, 
  CaretDown, 
  CaretUp,
  ArrowsClockwise,
  Eye
} from "@phosphor-icons/react";
import Select from 'react-select';

const PackageReports = () => {
    const dispatch = useDispatch();
    const { packageReports, loading } = useSelector(state => state.packageSlice);
    const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
    const [filteredReports, setFilteredReports] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const paginationPerPage = 10;
    const [selectedPackageType, setSelectedPackageType] = useState(null);
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [showDayModal, setShowDayModal] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [selectedDayNumber, setSelectedDayNumber] = useState(null);
    const [showAllDaysModal, setShowAllDaysModal] = useState(false);
    const [selectedPackageForAllDays, setSelectedPackageForAllDays] = useState(null);
    const [packageNameFilter, setPackageNameFilter] = useState(null);
    const [ratingFilter, setRatingFilter] = useState('');

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '0.375rem',
            boxShadow: 'none',
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            },
            height: '50px'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? 'rgba(13, 110, 253, 0.1)' : null,
            color: state.isSelected ? 'white' : 'inherit'
        })
    };

    const getPackageTypeOptions = () => [
        { label: 'Default', value: 'default' },
        { label: 'Custom', value: 'custom' }
    ];

    const getPackageNameOptions = () => {
        if (!packageReports) return [];
        
        // Get unique package names
        const uniquePackageNames = [...new Set(packageReports.map(report => report.package_name))];
        
        return uniquePackageNames.map(name => ({
            label: name,
            value: name
        }));
    };

    const filterReportsByDate = (data) => {
        if (!data || data.length === 0) return [];
        
        return data.filter(report => {
            const reportDate = moment(report.created_at);
            return reportDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
        });
    };

    const calculateStats = (data) => {
        if (!data || data.length === 0) return {
            totalOrders: 0,
            totalAmount: 0,
            averageRating: 0,
            defaultPackages: 0,
            customPackages: 0
        };
        
        const totalOrders = data.length;
        const totalAmount = data.reduce((sum, report) => sum + (parseFloat(report.total_amount) || 0), 0);
        const averageRating = data.reduce((sum, report) => sum + (parseFloat(report.rating) || 0), 0) / totalOrders;
        const defaultPackages = data.filter(item => item.package_type === 'default').length;
        const customPackages = data.filter(item => item.package_type === 'custom').length;

        return {
            totalOrders,
            totalAmount: totalAmount.toFixed(2),
            averageRating: averageRating.toFixed(1),
            defaultPackages,
            customPackages
        };
    };

    const handleSort = (column) => {
        const newDirection = sortField === column && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(column);
        setSortDirection(newDirection);
    };

    const getSortedData = (data) => {
        if (!data || data.length === 0) return [];
        
        return [...data].sort((a, b) => {
            let aValue, bValue;
            
            switch(sortField) {
                case 'created_at':
                    aValue = new Date(a.created_at);
                    bValue = new Date(b.created_at);
                    break;
                case 'package_name':
                    aValue = (a.package_name || '').toLowerCase();
                    bValue = (b.package_name || '').toLowerCase();
                    break;
                case 'total_amount':
                    aValue = parseFloat(a.total_amount || 0);
                    bValue = parseFloat(b.total_amount || 0);
                    break;
                case 'rating':
                    aValue = parseFloat(a.rating || 0);
                    bValue = parseFloat(b.rating || 0);
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };


    useEffect(() => {
        if (!packageReports) return;
        
        let filtered = filterReportsByDate(packageReports);

        if (selectedPackageType) {
            filtered = filtered.filter(report => report.package_type === selectedPackageType.value);
        }

        if (packageNameFilter) {
            filtered = filtered.filter(report => 
                report.package_name === packageNameFilter.value
            );
        }

        if (ratingFilter) {
            filtered = filtered.filter(report => 
                report.rating.toString() === ratingFilter
            );
        }
        
        setFilteredReports(filtered);
        setCurrentPage(1);
    }, [packageReports, startDate, endDate, selectedPackageType, packageNameFilter, ratingFilter]);

    useEffect(() => {
        dispatch(getPackageReportsDetails());
    }, [dispatch]);

    const resetFilters = () => {
        setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
        setEndDate(moment().endOf('month').format('YYYY-MM-DD'));
        setSelectedPackageType(null);
        setPackageNameFilter(null);
        setRatingFilter('');
        setSortField('created_at');
        setSortDirection('desc');
    };


    const handleViewAllDays = (packageData) => {
        setSelectedPackageForAllDays(packageData);
        setShowAllDaysModal(true);
    };


    const AllDaysModal = () => {
        if (!selectedPackageForAllDays) return null;

        return (
            <Modal show={showAllDaysModal} onHide={() => setShowAllDaysModal(false)} size="lg" className="package-details-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="text-primary">
                        <h4 className="mb-0">{selectedPackageForAllDays.package_name}</h4>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {/* Package Information Card */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <h6 className="text-primary mb-3">Package Information</h6>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <small className="text-muted d-block">Package Name</small>
                                        <strong>{selectedPackageForAllDays.package_name}</strong>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <small className="text-muted d-block">Date Range</small>
                                        <strong>
                                            {moment(selectedPackageForAllDays.start_date).format('DD/MM/YYYY')} - {moment(selectedPackageForAllDays.end_date).format('DD/MM/YYYY')}
                                        </strong>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <small className="text-muted d-block">Package Type</small>
                                        <span className={`badge ${selectedPackageForAllDays.package_type === 'default' ? "bg-info" : "bg-primary"}`}>
                                            {selectedPackageForAllDays.package_type === 'default' ? "Default" : "Custom"}
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <small className="text-muted d-block">Total Amount</small>
                                        <strong className="text-success">₹{selectedPackageForAllDays.total_amount}</strong>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Days Details Section */}
                    <div>
                        <h6 className="text-primary mb-3">Day-wise Details</h6>
                        <div className="days-container">
                            {selectedPackageForAllDays.day_summary.map((day, index) => (
                                <Card key={index} className="border-0 shadow-sm mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="mb-0">
                                                <span className="badge bg-primary me-2">Day {day.day_no}</span>
                                            </h6>
                                        </div>
                                        
                                        {day.details && (
                                            <div className="mb-3">
                                                <small className="text-muted d-block mb-1">Details</small>
                                                <p className="mb-0">{day.details}</p>
                                            </div>
                                        )}

                                        <div>
                                            <small className="text-muted d-block mb-2">Temples</small>
                                            <div className="temple-chips">
                                                {day.temple_names && day.temple_names?.length > 0 ? (    
                                                    day.temple_names.map((temple, templeIndex) => (
                                                        <span key={templeIndex} className="badge bg-light text-dark me-2 mb-2 p-2">
                                                            {temple}
                                                        </span>
                                                    ))
                                                ) : (
                                                    day.temples?.map((temple, templeIndex) => (
                                                        <span key={templeIndex} className="badge bg-light text-dark me-2 mb-2 p-2">
                                                            {temple}
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowAllDaysModal(false)}
                        className="px-4"
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        const stats = calculateStats(filteredReports);
        
        doc.setFont('helvetica');
        doc.setFontSize(16);
        doc.text('Package Reports', 105, 15, { align: 'center' });
        
        doc.setFontSize(10);
        doc.text(`From: ${moment(startDate).format('DD/MM/YYYY')} To: ${moment(endDate).format('DD/MM/YYYY')}`, 14, 25);
        
        // Summary section
        doc.setFontSize(12);
        doc.text(`Total Orders: ${stats.totalOrders}`, 14, 35);
        doc.text(`Total Amount: ₹${stats.totalAmount}`, 14, 42);
        doc.text(`Average Rating: ${stats.averageRating}`, 14, 49);
        
        const tableData = filteredReports.map(report => {
            const daySummary = selectedDayNumber 
                ? (report.day_summary || []).filter(day => day.day_no === selectedDayNumber)
                : (report.day_summary || []);

            return [
                report.package_name || '-',
                report.user_name || '-',
                `${moment(report.start_date).format('DD/MM/YYYY')} - ${moment(report.end_date).format('DD/MM/YYYY')}`,
                report.package_type === 'default' ? 'Default' : 'Custom',
                `₹${report.total_amount || 0}`,
                report.rating?.toString() || '0',
                daySummary.map(day => 
                    `Day ${day.day_no}: ${(day.temple_names || []).join(', ')}`
                ).join('\n')
            ];
        });
        
        autoTable(doc, {
            head: [columns.map(col => typeof col.name === 'object' ? col.name.props.children[0] : col.name)],
            body: tableData,
            startY: 60,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [13, 110, 253] },
            margin: { top: 60 }
        });
        
        doc.save(`Package_Reports_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    const exportToExcel = () => {
        const stats = calculateStats(filteredReports);
        
        const summaryData = [
            ['Package Reports'],
            [`From: ${moment(startDate).format('DD/MM/YYYY')} To: ${moment(endDate).format('DD/MM/YYYY')}`],
            [''],
            ['Summary'],
            ['Total Orders', stats.totalOrders],
            ['Total Amount', `₹${stats.totalAmount}`],
            ['Average Rating', stats.averageRating],
            ['Default Packages', stats.defaultPackages],
            ['Custom Packages', stats.customPackages],
            [''],
            ['Package Details']
        ];

        const reportData = [
            columns.map(col => typeof col.name === 'object' ? col.name.props.children[0] : col.name),
            ...filteredReports.map(report => {
                const daySummary = selectedDayNumber 
                    ? (report.day_summary || []).filter(day => day.day_no === selectedDayNumber)
                    : (report.day_summary || []);

                return [
                    report.package_name || '-',
                    report.user_name || '-',
                    `${moment(report.start_date).format('DD/MM/YYYY')} - ${moment(report.end_date).format('DD/MM/YYYY')}`,
                    report.package_type === 'default' ? 'Default' : 'Custom',
                    report.total_amount || 0,
                    report.rating || 0,
                    daySummary.map(day => 
                        `Day ${day.day_no}: ${(day.temple_names || []).join(', ')}`
                    ).join('\n')
                ];
            })
        ];

        const excelData = [...summaryData, ...reportData];
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(excelData);
        
        const wscols = [
            { wch: 20 }, 
            { wch: 20 }, 
            { wch: 20 }, 
            { wch: 15 }, 
            { wch: 15 }, 
            { wch: 10 }, 
            { wch: 50 }, 
        ];
        ws['!cols'] = wscols;

        XLSX.utils.book_append_sheet(wb, ws, 'Package Reports');
        XLSX.writeFile(wb, `Package_Reports_${moment().format('YYYY-MM-DD')}.xlsx`);
    };

    const sortedData = getSortedData(filteredReports);
    const paginatedData = sortedData.slice(
        (currentPage - 1) * paginationPerPage,
        currentPage * paginationPerPage
    );

    const stats = calculateStats(filteredReports);

    const columns = [
        {
            name: "Package Name",
            selector: (row) => row.package_name,
            sortable: true,
        },
        
        {
            name: "Date Range",
            selector: (row) => `${moment(row.start_date).format('DD/MM/YYYY')} - ${moment(row.end_date).format('DD/MM/YYYY')}`,
            sortable: false,
        },
        {
            name: "Package Type",
            selector: (row) => row.package_type,
            cell: (row) => (
                <span className={`badge ${row.package_type === 'default' ? "bg-info" : "bg-primary"}`}>
                    {row.package_type === 'default' ? "Default" : "Custom"}
                </span>
            ),
            sortable: false,
        },
        {
            name: "Total Amount",
            selector: (row) => row.total_amount,
            cell: (row) => <span className="fw-bold">₹{row.total_amount}</span>,
            sortable: true,
        },
        {
            name: "Rating",
            selector: (row) => row.rating,
            cell: (row) => (
                <div className="d-flex align-items-center">
                    <span className="text-warning me-1">★</span>
                    {row.rating}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Days",
            selector: (row) => row.day_summary,
            cell: (row) => (
                <div className="temple-list">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className='mr-2'>
                            <strong>{row.day_summary.length}</strong>
                        </div>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            style={{marginLeft: '10px'}}
                            onClick={() => handleViewAllDays(row)}
                        >
                            <Eye size={16} className="me-1" />
                        </Button>
                    </div>
                   
                </div>
            ),
            sortable: false,
        }
    ];

    return (
        <Container fluid className="p-3">
            <Card className="shadow-sm border-0">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0">Package Reports</h4>
                        <div className="d-flex">
                            <Button
                                variant="outline-secondary"
                                className="me-2 d-flex align-items-center"
                                onClick={resetFilters}
                            >
                                <ArrowsClockwise size={16} className="me-1" /> Reset Filters
                            </Button>
                            <Button
                                variant="outline-primary"
                                className="icon-button me-2"
                                onClick={exportToExcel}
                                disabled={filteredReports.length === 0}
                                title="Export to Excel"
                            >
                                <FileXls size={20} className="me-1" /> Excel
                            </Button>
                            <Button
                                variant="outline-danger"
                                className="icon-button"
                                onClick={exportToPDF}
                                disabled={filteredReports.length === 0}
                                title="Export to PDF"
                            >
                                <FilePdf size={20} className="me-1" /> PDF
                            </Button>
                        </div>
                    </div>

                    <Card className="bg-light mb-4">
                        <Card.Body>
                            <h5 className="mb-3">Filters</h5>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="d-flex align-items-center">
                                            <Calendar size={16} className="me-1" /> From Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="d-flex align-items-center">
                                            <Calendar size={16} className="me-1" /> To Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="d-flex align-items-center">
                                            <Package size={16} className="me-1" /> Package Type
                                        </Form.Label>
                                        <Select
                                            isClearable
                                            value={selectedPackageType}
                                            onChange={setSelectedPackageType}
                                            options={getPackageTypeOptions()}
                                            placeholder="All Package Types"
                                            styles={customSelectStyles}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="d-flex align-items-center">
                                            <Package size={16} className="me-1" /> Package Name
                                        </Form.Label>
                                        <Select
                                            isClearable
                                            value={packageNameFilter}
                                            onChange={setPackageNameFilter}
                                            options={getPackageNameOptions()}
                                            placeholder="Search package name"
                                            styles={customSelectStyles}
                                            isSearchable
                                            noOptionsMessage={() => "No package names found"}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="d-flex align-items-center">
                                            <span className="text-warning me-1">★</span> Rating
                                        </Form.Label>
                                        <Form.Select
                                            value={ratingFilter}
                                            onChange={(e) => setRatingFilter(e.target.value)}
                                        >
                                            <option value="">All Ratings</option>
                                            <option value="5">5 Stars</option>
                                            <option value="4">4 Stars</option>
                                            <option value="3">3 Stars</option>
                                            <option value="2">2 Stars</option>
                                            <option value="1">1 Star</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="mb-4">
                        <Col md={2}>
                            <Card className="text-center h-100 border-0 shadow-sm stats-card">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Total Orders</h6>
                                    <h3 className="mb-0 text-primary">{stats.totalOrders}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center h-100 border-0 shadow-sm stats-card">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Total Amount</h6>
                                    <h3 className="mb-0 text-primary">₹{stats.totalAmount}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="text-center h-100 border-0 shadow-sm stats-card">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Average Rating</h6>
                                    <h3 className="mb-0 text-primary">{stats.averageRating}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="text-center h-100 border-0 shadow-sm stats-card">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Default Packages</h6>
                                    <h3 className="mb-0 text-primary">{stats.defaultPackages}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="text-center h-100 border-0 shadow-sm stats-card">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Custom Packages</h6>
                                    <h3 className="mb-0 text-primary">{stats.customPackages}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="border-0 shadow-sm">
                        <Card.Body>
                            <DataTable
                                columns={columns}
                                data={paginatedData}
                                pagination
                                paginationServer
                                paginationTotalRows={filteredReports.length}
                                onChangePage={(page) => setCurrentPage(page)}
                                paginationPerPage={paginationPerPage}
                                highlightOnHover
                                pointerOnHover
                                progressPending={loading}
                                noDataComponent={
                                    <div className="p-5 text-center text-muted">
                                        <p className="mb-0">No package reports found for the selected filters</p>
                                    </div>
                                }
                                progressComponent={
                                    <div className="w-100 d-flex p-4">
                                        <div className="spinner-border text-primary mx-auto" role="status"></div>
                                    </div>
                                }
                                customStyles={{
                                    headRow: {
                                        style: {
                                            backgroundColor: '#f8f9fa',
                                            borderTopStyle: 'none',
                                            borderBottomStyle: 'solid',
                                            borderBottomWidth: '1px',
                                            borderBottomColor: '#e9ecef',
                                        },
                                    },
                                    headCells: {
                                        style: {
                                            fontWeight: '600',
                                            color: '#495057',
                                            paddingLeft: '16px',
                                            paddingRight: '16px',
                                        },
                                    },
                                    rows: {
                                        style: {
                                            minHeight: '55px',
                                            fontSize: '14px',
                                        },
                                    },
                                    cells: {
                                        style: {
                                            paddingLeft: '16px',
                                            paddingRight: '16px',
                                        },
                                    },
                                }}
                            />
                        </Card.Body>
                    </Card>
                </Card.Body>
            </Card>

            <AllDaysModal />
        </Container>
    );
};

export default PackageReports;
